import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import FormCard from '../../../shared/components/custom/FormCard';
import Api from '../../../util/api';
import { formatDateTime } from '../../../util/date-and-time';
import { partial } from '../../../util/translation/translation';

const p = partial('chat');

// What a log entry's details say, in one line
const describe = (entry) => {
  const d = entry.details || {};
  switch (entry.action) {
    case 'user_blocked':
      return [
        d.userName,
        d.expiresAt
          ? p('blockedUntilTime', { time: formatDateTime(d.expiresAt) })
          : p('blockDuration_permanent'),
        d.reason,
      ]
        .filter(Boolean)
        .join(' · ');
    case 'messages_expired':
      return p('logExpiredDetails', { count: d.deleted, months: d.months });
    case 'message_auto_hidden':
      return p('reportCount', { count: d.reports });
    default:
      return [d.name, d.text].filter(Boolean).join(': ');
  }
};

// Who did what in chat moderation, newest first
const ModerationLogCard = ({ refreshKey }) => {
  const [entries, setEntries] = useState([]);
  const [hasMore, setHasMore] = useState(false);

  const load = useCallback(async (before) => {
    try {
      const page = await Api.chat.getModerationLog({ before });
      setEntries((current) => (before ? [...current, ...page] : page));
      setHasMore(page.length >= 50);
    } catch {
      if (!before) setEntries([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load, refreshKey]);

  return (
    <FormCard
      title={p('moderationLog')}
      bodyClassName="form-card__body--flush"
      className="mb-3"
    >
      <Table responsive striped className="table--roomy table--stack">
        <thead>
          <tr>
            <th>{p('logWhen')}</th>
            <th>{p('logWho')}</th>
            <th>{p('logAction')}</th>
            <th>{p('logDetails')}</th>
          </tr>
        </thead>
        <tbody>
          {entries.length === 0 ? (
            <tr>
              <td colSpan={4} className="text-muted">
                {p('logEmpty')}
              </td>
            </tr>
          ) : (
            entries.map((entry) => (
              <tr key={entry.id}>
                <td className="text-nowrap table-stack__primary">
                  {formatDateTime(entry.createdAt)}
                </td>
                <td data-label={p('logWho')}>
                  {entry.actorId === 'system' && p('logSystem')}
                  {entry.actorId !== 'system' && (
                    <Link to={`/users/edit/${entry.actorId}`}>
                      {entry.actorEmail || entry.actorId}
                    </Link>
                  )}
                </td>
                <td data-label={p('logAction')}>
                  {p(`logAction_${entry.action}`)}
                  {entry.targetUserId && (
                    <>
                      {' · '}
                      <Link to={`/users/edit/${entry.targetUserId}`}>
                        {p('user')}
                      </Link>
                    </>
                  )}
                </td>
                <td
                  data-label={p('logDetails')}
                  style={{ maxWidth: 420, whiteSpace: 'pre-wrap' }}
                >
                  {describe(entry)}
                </td>
              </tr>
            ))
          )}
        </tbody>
      </Table>
      {hasMore && (
        <div className="p-2 text-center">
          <Button
            size="sm"
            className="mb-0"
            onClick={() => load(entries[entries.length - 1]?.createdAt)}
          >
            {p('loadMore')}
          </Button>
        </div>
      )}
    </FormCard>
  );
};

export default ModerationLogCard;
